/** Parent / guardian user checks and assigned-bus mapping. */

export function isParentUserType(user, userTypeFallback = '') {
  const t = String(user?.user_type || userTypeFallback || '').toLowerCase()
  return t === 'parents' || t === 'parent'
}

export function getPickupAddress(...sources) {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue
    const value =
      source.pickup ??
      source.pick_up ??
      source.pickup_address ??
      source.pick_up_address ??
      source.pickup_point
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

function asList(payload) {
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  if (Array.isArray(payload?.data?.buses)) return payload.data.buses
  if (Array.isArray(payload?.data?.students)) return payload.data.students
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.buses)) return payload.buses
  if (Array.isArray(payload?.students)) return payload.students
  if (Array.isArray(payload)) return payload
  if (payload?.data && typeof payload.data === 'object') return [payload.data]
  return []
}

function pushPositiveId(ids, value) {
  const n = Number(value)
  if (Number.isFinite(n) && n > 0) ids.push(n)
}

function getStudentIdsFromItem(item) {
  const ids = []
  if (!item || typeof item !== 'object') return ids
  pushPositiveId(ids, item.student_id)
  pushPositiveId(ids, item.studentId)
  pushPositiveId(ids, item?.student?.id)
  pushPositiveId(ids, item?.student?.student_id)
  const nested = Array.isArray(item.students)
    ? item.students
    : Array.isArray(item.children)
      ? item.children
      : []
  nested.forEach((s) => {
    pushPositiveId(ids, s?.id)
    pushPositiveId(ids, s?.student_id)
  })
  return ids
}

function findMatchingStudentRecord(item, studentId) {
  const sid = Number(studentId)
  if (!item || !Number.isFinite(sid) || sid <= 0) return null
  if (Number(item.student_id) === sid || Number(item.studentId) === sid) return item
  if (
    item.student &&
    (Number(item.student.id) === sid || Number(item.student.student_id) === sid)
  ) {
    return item.student
  }
  const nested = Array.isArray(item.students)
    ? item.students
    : Array.isArray(item.children)
      ? item.children
      : []
  return (
    nested.find((s) => Number(s?.id) === sid || Number(s?.student_id) === sid) ||
    null
  )
}

function getBusId(item) {
  const bus = item?.bus || item?.transport || item
  return bus?.bus_id ?? bus?.id ?? item?.bus_id ?? item?.assign_bus
}

function isPositiveId(value) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0
}

/** true = assigned, false = explicitly unassigned, null = unknown */
export function studentHasAssignedBus(profile) {
  if (!profile || typeof profile !== 'object') return null
  if (!Object.prototype.hasOwnProperty.call(profile, 'assign_bus')) return null
  return isPositiveId(profile.assign_bus)
}

/**
 * Map GET /parents/students/bus-details into the assigned-transport cards
 * shown on a single child's profile.
 */
export function mapParentAssignedTransports(payload, studentId, profile = null) {
  if (studentHasAssignedBus(profile) === false) return []

  const list = asList(payload)
  const sid = Number(studentId)
  const assignedBusId = Number(profile?.assign_bus)

  const mapped = []
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    const bus = item.bus || item.transport || item
    const busId = getBusId(item)
    const studentIds = getStudentIdsFromItem(item)
    const studentRecord = findMatchingStudentRecord(item, sid)

    if (studentHasAssignedBus(studentRecord) === false) continue

    if (isPositiveId(assignedBusId)) {
      if (Number(busId) !== assignedBusId) continue
    } else if (studentIds.length > 0) {
      if (!isPositiveId(sid) || !studentIds.includes(sid)) continue
    } else {
      continue
    }

    mapped.push({
      id: `assigned-bus-${busId || mapped.length}`,
      bus_id: busId != null ? String(busId) : '',
      bus_number: bus.bus_number || item.bus_number || '',
      driver_name: bus.driver_name || item.driver_name || '',
      driver_contact_number:
        bus.driver_contact_number || item.driver_contact_number || '',
      pickup: getPickupAddress(studentRecord, item, bus, profile),
    })
  }

  const seen = new Set()
  return mapped.filter((t) => {
    const key = t.bus_id || t.id
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
